import { useEffect, useRef } from 'react';

import { useSetAtom } from 'jotai';

import { createDebugLogger } from '@videa/utils';

import {
  type PendingRecoverySession,
  pendingRecoverySessionAtom,
} from '../atoms/recording';
import { VideaNotesOPFS, opfs } from '../storage/opfs';

const debug = createDebugLogger('RecoveryCheck');

interface UseRecoveryCheckOptions {
  /**
   * Whether the recovery check is enabled.
   * Should be controlled by feature flag (e.g., gateNotesLocalAsyncUpload).
   */
  enabled?: boolean;
}

/**
 * Hook that checks for recoverable recording sessions on mount.
 * When an interrupted session is found, it sets the pendingRecoverySessionAtom
 * to trigger the RecordingRecoveryModal.
 *
 * Should be used in conjunction with RecordingRecoveryModal component.
 */
export const useRecoveryCheck = ({
  enabled = true,
}: UseRecoveryCheckOptions = {}) => {
  const setPendingSession = useSetAtom(pendingRecoverySessionAtom);
  const hasCheckedRef = useRef(false);

  useEffect(() => {
    // Only check once per mount
    if (hasCheckedRef.current || !enabled) {
      return;
    }

    // Check for OPFS support
    if (!VideaNotesOPFS.isAvailable()) {
      debug.log('OPFS not available, skipping recovery check');
      return;
    }

    const checkForRecovery = async () => {
      try {
        debug.log('Checking for recoverable sessions...');
        const recoverableSessions = await opfs.findRecoverableSessions();

        if (recoverableSessions.length > 0) {
          // Take the most recent session for recovery
          const sortedSessions = recoverableSessions.sort(
            (a, b) => b.createdAt - a.createdAt,
          );
          const session = sortedSessions[0];

          if (session) {
            debug.log('Found recoverable session:', session.id);

            const pendingSession: PendingRecoverySession = {
              id: session.id,
              createdAt: session.createdAt,
              // Convert null to undefined for compatibility
              unifiedAppointmentId: session.unifiedAppointmentId ?? undefined,
              standalonePatientVisitId:
                session.standalonePatientVisitId ?? undefined,
              patientVideaId: session.patientVideaId ?? undefined,
              organizationId: session.organizationId ?? undefined,
              practiceId: session.practiceId ?? undefined,
              chunkCount: session.chunkCount,
              lastTranscriptContext: session.lastTranscriptContext ?? undefined,
              templateId: session.templateId ?? undefined,
              promptId: session.promptId ?? undefined,
              patientFirstName: session.patientFirstName ?? undefined,
              patientLastName: session.patientLastName ?? undefined,
              ambientSessionId: session.ambientSessionId ?? undefined,
            };

            setPendingSession(pendingSession);
          }
        } else {
          debug.log('No recoverable sessions found');
        }
      } catch (error) {
        debug.error('Failed to check for recoverable sessions:', error);
      }

      // Clean up sessions older than 48 hours — these are too stale to recover
      // (token expired, server session timed out) and would otherwise leak in OPFS.
      try {
        await opfs.cleanupStale(48 * 60 * 60 * 1000);
      } catch (error) {
        debug.error('Failed to clean up stale sessions:', error);
      }
    };

    hasCheckedRef.current = true;
    checkForRecovery();
  }, [enabled, setPendingSession]);
};
